/* ============================================================================
 * MTE - Mission progress (persistence localStorage, namespaced par kidId).
 * ----------------------------------------------------------------------------
 * Wrapper minimaliste autour de localStorage. Pas de zustand pour cette
 * feature (Vague 2 - flow end-to-end). Une vraie migration vers zustand
 * viendra quand MultiProfileAuthAdapter sera implemente (Vague 3).
 *
 * Conventions :
 *   STORAGE_KEY versionne (v1). Ne JAMAIS casser une cle - migrer via v2.
 *   Storage namespaced par kidId : mte:mission-progress:v1:<kidId>.
 *   Backward compat : kidId == 'default' utilise l\u2019ancienne cle v1.
 *   loadProgress() retourne TOUJOURS un objet (default ou saved).
 *   saveProgress() merge par-dessus l\u2019existant pour eviter ecrasures.
 *   selectQuestionsForStep() : shuffle deterministe base sur kidId + stepId,
 *      meme ordre pour un meme (kid, step) tant que stepResults inchanges.
 * ========================================================================== */

import type { Question, Matiere } from '../data/banque-questions-ce1.legacy';
import { getQuestionsByMatiere } from '../data/banque-questions-ce1.legacy';
import type { BanqueItem } from '../lib/banque-types';
import type { PreviousLevel } from '../lib/diagnostic-runtime';
import { BANQUE_CP } from '../data/banque-questions-cp';
import { BANQUE_CE1 } from '../data/banque-questions-ce1';
import { BANQUE_CE2 } from '../data/banque-questions-ce2';
import { BANQUE_CM1 } from '../data/banque-questions-cm1';

export interface AnswerRecord {
  questionId: string;
  reponse: string;
  correct: boolean;
}

export interface StepResult {
  stepId: number;
  matiere: Matiere;
  answers: AnswerRecord[];
  score: number;
  completedAt: string;
  selectedOrder: string[];
}

export interface MissionProgress {
  currentStep: number;
  completedSteps: number[];
  stepResults: Record<number, StepResult>;
  totalScore: number;
}

const STORAGE_KEY_V1_LEGACY = 'mte:mission-progress:v1';
const STORAGE_KEY_V1_PREFIX = 'mte:mission-progress:v1:';
const DEFAULT_KID_ID = 'default';
const TOTAL_STEPS = 15;

const BANK_BY_PREVIOUS: Record<PreviousLevel, ReadonlyArray<BanqueItem>> = {
  CP: BANQUE_CP,
  CE1: BANQUE_CE1,
  CE2: BANQUE_CE2,
  CM1: BANQUE_CM1,
};

const SEEN_KEY_PREFIX = 'mte:mission:seen:v1:';

function getSeenKey(kidId: string): string {
  return SEEN_KEY_PREFIX + kidId;
}

function loadSeenIds(kidId: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(getSeenKey(kidId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function saveSeenIds(kidId: string, ids: Set<string>): void {
  try {
    window.localStorage.setItem(getSeenKey(kidId), JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

function previousBankForProfile(profileSchoolLevel: 'CE1' | 'CM1' | 'CE2' | 'CP' | 'CM2'): PreviousLevel {
  const map: Record<string, PreviousLevel> = {
    CE1: 'CP',
    CE2: 'CE1',
    CM1: 'CE2',
    CP: 'CP',
  };
  return map[profileSchoolLevel] ?? 'CE1';
}

export function getBankForSchoolLevel(profileSchoolLevel: 'CE1' | 'CM1' | 'CE2' | 'CP' | 'CM2'): ReadonlyArray<BanqueItem> {
  const previous = previousBankForProfile(profileSchoolLevel);
  return BANK_BY_PREVIOUS[previous] ?? BANQUE_CE1;
}


/** Default progress for a brand-new profile (empty mission). */
function emptyProgress(): MissionProgress {
  return {
    currentStep: 1,
    completedSteps: [],
    stepResults: {},
    totalScore: 0,
  };
}

/** Backward-compat default for the legacy (single-profile) v1 key. */
function legacyDefaultProgress(): MissionProgress {
  return {
    currentStep: 3,
    completedSteps: [1, 2],
    stepResults: {},
    totalScore: 0,
  };
}

/** Hash deterministe simple (xmur3) pour seedre le shuffle. */
function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i += 1) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/** Mulberry32 - PRNG seedable leger. */
function mulberry32(a: number): () => number {
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Safe localStorage access (SSR / sandbox tests). */
function lsGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore (quota, disabled) */
  }
}

function lsUnset(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Public : construit la cle localStorage pour un kidId donne. */
export function getMissionStorageKey(kidId: string): string {
  if (!kidId || typeof kidId !== 'string') {
    throw new Error('[mission-progress] getMissionStorageKey: kidId must be a non-empty string');
  }
  if (kidId === DEFAULT_KID_ID) return STORAGE_KEY_V1_LEGACY;
  return STORAGE_KEY_V1_PREFIX + kidId;
}

function resolveDefaultFor(kidId: string): MissionProgress {
  return kidId === DEFAULT_KID_ID ? legacyDefaultProgress() : emptyProgress();
}

export function loadProgress(kidId: string): MissionProgress {
  if (!kidId || typeof kidId !== 'string') {
    throw new Error('[mission-progress] loadProgress: kidId must be a non-empty string');
  }
  const key = getMissionStorageKey(kidId);
  const raw = lsGet(key);
  if (!raw) return resolveDefaultFor(kidId);
  try {
    const parsed = JSON.parse(raw) as Partial<MissionProgress>;
    const fallback = resolveDefaultFor(kidId);
    return {
      currentStep: typeof parsed.currentStep === 'number' ? parsed.currentStep : fallback.currentStep,
      completedSteps: Array.isArray(parsed.completedSteps)
        ? parsed.completedSteps.filter((n): n is number => typeof n === 'number')
        : [],
      stepResults:
        parsed.stepResults && typeof parsed.stepResults === 'object'
          ? (parsed.stepResults as Record<number, StepResult>)
          : {},
      totalScore: typeof parsed.totalScore === 'number' ? parsed.totalScore : 0,
    };
  } catch {
    return resolveDefaultFor(kidId);
  }
}

export function saveProgress(kidId: string, p: MissionProgress): void {
  if (!kidId || typeof kidId !== 'string') {
    throw new Error('[mission-progress] saveProgress: kidId must be a non-empty string');
  }
  const key = getMissionStorageKey(kidId);
  lsSet(key, JSON.stringify(p));
}

export function resetProgress(kidId: string): void {
  if (!kidId || typeof kidId !== 'string') {
    throw new Error('[mission-progress] resetProgress: kidId must be a non-empty string');
  }
  lsUnset(getMissionStorageKey(kidId));
}

/**
 * Selectionne 10 questions (5 FR + 5 MA) dans un ordre deterministe
 * base sur kidId + stepId. Dedup entre etapes via seenIds.
 *
 * IMPORTANT: prefer banque Lumen via TARGET_TO_PREVIOUS_BANK quand profileSchoolLevel
 * est fourni. Sinon fallback legacy sur ce1.legacy.ts (backward compat).
 */
export function selectQuestionsForStep(
  kidId: string,
  stepId: number,
  profileSchoolLevel?: 'CE1' | 'CM1' | 'CE2' | 'CP' | 'CM2'
): Question[] {
  if (!kidId || typeof kidId !== 'string') {
    throw new Error('[mission-progress] selectQuestionsForStep: kidId must be a non-empty string');
  }

  // Chemin LUMEN (preferred): dedup-aware, mapping correct
  // Filtre pour ne garder que QCM et VF (compat UI Mission Rentrée legacy)
  if (profileSchoolLevel) {
    const bank = getBankForSchoolLevel(profileSchoolLevel);
    const seen = loadSeenIds(kidId);
    const isMCCompatible = (it: BanqueItem) => it.archetype === 'qcm' || it.archetype === 'vf';
    const frItems = bank.filter((it) => {
      const cat = (it as BanqueItem & { matiere?: string }).matiere ?? guessMatiere(it);
      return cat === 'FR' && !seen.has(it.id) && isMCCompatible(it);
    });
    const maItems = bank.filter((it) => {
      const cat = (it as BanqueItem & { matiere?: string }).matiere ?? guessMatiere(it);
      return cat === 'MA' && !seen.has(it.id) && isMCCompatible(it);
    });

    const rng = mulberry32(xmur3('kid-' + kidId + '-step-' + stepId)());
    const shuffle = <T,>(arr: T[]): T[] => {
      const out = [...arr];
      for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        [out[i], out[j]] = [out[j]!, out[i]!];
      }
      return out;
    };

    const fr5 = shuffle(frItems).slice(0, 5);
    const ma5 = shuffle(maItems).slice(0, 5);

    // Si pas assez d'items unseen (pool epuise), reset dedup
    if (fr5.length < 5 || ma5.length < 5) {
      window.localStorage.removeItem(getSeenKey(kidId));
      return selectQuestionsForStep(kidId, stepId, profileSchoolLevel);
    }

    const selected = [...fr5, ...ma5];

    // Convertir BanqueItem → Question (legacy type) pour compat MissionEtape.
    // Si pas assez d'items QCM/VF/Saisie, complete avec legacy fallback.
    const converted: Question[] = selected
      .map(banqueItemToLegacyQuestion)
      .filter((q): q is Question => q !== null);
    if (converted.length < 10) {
      const fr = [...getQuestionsByMatiere('FR')];
      const ma = [...getQuestionsByMatiere('MA')];
      const legacyRng = mulberry32(xmur3('kid-' + kidId + '-step-' + stepId + '-legacy')());
      const legacyShuffle = <T,>(arr: T[]): T[] => {
        const out = [...arr];
        for (let i = out.length - 1; i > 0; i -= 1) {
          const j = Math.floor(legacyRng() * (i + 1));
          [out[i], out[j]] = [out[j]!, out[i]!];
        }
        return out;
      };
      const frNeeded = Math.max(0, 5 - converted.filter((q: Question) => q.matiere === 'FR').length);
      const maNeeded = Math.max(0, 5 - converted.filter((q: Question) => q.matiere === 'MA').length);
      const frLegacy = legacyShuffle(fr).slice(0, frNeeded);
      const maLegacy = legacyShuffle(ma).slice(0, maNeeded);
      converted.push(...frLegacy, ...maLegacy);
    }
    const final = converted.slice(0, 10);

    const newSeen = new Set(seen);
    selected.forEach((it: BanqueItem) => newSeen.add(it.id));
    saveSeenIds(kidId, newSeen);

    return final;
  }

  // Legacy fallback (no profile context): pool 20 Vague 2
  const fr = [...getQuestionsByMatiere('FR')];
  const ma = [...getQuestionsByMatiere('MA')];
  const rng = mulberry32(xmur3('kid-' + kidId + '-step-' + stepId)());
  const shuffle = <T,>(arr: T[]): T[] => {
    const out = [...arr];
    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rng() * (i + 1));
      [out[i], out[j]] = [out[j]!, out[i]!];
    }
    return out;
  };
  const fr5 = shuffle(fr).slice(0, 5);
  const ma5 = shuffle(ma).slice(0, 5);
  return [...fr5, ...ma5];
}

function guessMatiere(item: BanqueItem): 'FR' | 'MA' {
  const id = (item.id || '').toUpperCase();
  if (id.startsWith('FR')) return 'FR';
  if (id.startsWith('MA')) return 'MA';
  if (id.startsWith('LAN')) return 'FR';
  return 'FR';
}

function banqueItemToLegacyQuestion(item: BanqueItem): Question | null {
  const matiere = guessMatiere(item);
  const enonce = item.enonce;
  // Adapter only handles QCM, VF, Saisie archetypes (legacy compat)
  if (item.archetype === 'qcm') {
    return {
      id: item.id,
      matiere,
      enonce,
      options: item.options.slice(0, 4),
      bonneReponse: item.bonneReponse,
      difficulte: 1 as 1 | 2 | 3,
      explication: deriveExplicationFromItem(item.bonneReponse),
    };
  }
  if (item.archetype === 'vf') {
    return {
      id: item.id,
      matiere,
      enonce,
      options: ['Vrai', 'Faux'],
      bonneReponse: item.bonneReponse ? 'Vrai' : 'Faux',
      difficulte: 1 as 1 | 2 | 3,
      explication: deriveExplicationFromItem(item.bonneReponse ? 'Vrai' : 'Faux'),
    };
  }
  if (item.archetype === 'saisie') {
    return {
      id: item.id,
      matiere,
      enonce,
      options: [],
      bonneReponse: item.bonneReponse,
      difficulte: 1 as 1 | 2 | 3,
      explication: deriveExplicationFromItem(item.bonneReponse),
    };
  }
  return null;
}

function deriveExplicationFromItem(bonneReponse: string): string {
  return 'Bonne réponse : ' + bonneReponse;
}

export function clearSeenIds(kidId: string): void {
  try {
    window.localStorage.removeItem(getSeenKey(kidId));
  } catch {
    /* ignore */
  }
}

export const TOTAL_QUESTIONS_PER_STEP = 10;
export { TOTAL_STEPS };
